import React from 'react';
import { Select, InputNumber, Switch } from 'antd';
import type { InternalMedicineData } from '../../hooks/useConsultationForm';

interface Props {
  internalMedicineData: InternalMedicineData;
  setInternalMedicineField: (section: keyof InternalMedicineData, field: string, value: any) => void;
}

const STAGE_OPTIONS = [
  { value: 'Normal', label: 'Normal' },
  { value: 'Pre-HTA', label: 'Pre-HTA' },
  { value: 'Estadio 1', label: 'Estadio 1' },
  { value: 'Estadio 2', label: 'Estadio 2' },
  { value: 'Crisis', label: 'Crisis' },
];

const TREATMENT_OPTIONS = [
  { value: 'IECA', label: 'IECA' },
  { value: 'ARAII', label: 'ARAII' },
  { value: 'BCC', label: 'BCC' },
  { value: 'Diurético', label: 'Diurético' },
  { value: 'Betabloqueante', label: 'Betabloqueante' },
  { value: 'Otro', label: 'Otro' },
];

const ORGANS = ['Corazón', 'Riñón', 'Cerebro', 'Retina', 'Vasos'];

const STAGE_LABELS: Record<string, string> = {
  Normal: 'Normal',
  'Pre-HTA': 'Pre-HTA',
  'Estadio 1': 'Estadio 1',
  'Estadio 2': 'Estadio 2',
  Crisis: 'Crisis',
};

const STAGE_COLORS: Record<string, string> = {
  Normal: '#10b981',
  'Pre-HTA': '#f59e0b',
  'Estadio 1': '#f97316',
  'Estadio 2': '#ef4444',
  Crisis: '#dc2626',
};

const HypertensionAssessment: React.FC<Props> = ({ internalMedicineData, setInternalMedicineField }) => {
  const h = internalMedicineData.hypertension;
  const set = (field: string, value: any) => setInternalMedicineField('hypertension', field, value);

  const handleOrganToggle = (organ: string, present: boolean) => {
    const current = [...(h.target_organ_damage || [])];
    const idx = current.findIndex((o) => o.organ === organ);
    if (present) {
      if (idx >= 0) {
        current[idx] = { ...current[idx], present: true };
      } else {
        current.push({ organ, present: true, details: '' });
      }
    } else {
      if (idx >= 0) {
        current.splice(idx, 1);
      }
    }
    set('target_organ_damage', current);
  };

  const handleOrganDetails = (organ: string, details: string) => {
    const current = [...(h.target_organ_damage || [])];
    const idx = current.findIndex((o) => o.organ === organ);
    if (idx >= 0) {
      current[idx] = { ...current[idx], details };
    }
    set('target_organ_damage', current);
  };

  const getOrganDetails = (organ: string): string => {
    const found = (h.target_organ_damage || []).find((o) => o.organ === organ);
    return found?.details || '';
  };

  const isOrganPresent = (organ: string): boolean => {
    return (h.target_organ_damage || []).some((o) => o.organ === organ && o.present);
  };

  const calcColor = h.calculated_stage ? STAGE_COLORS[h.calculated_stage] || '#6b7280' : '#6b7280';
  const calcLabel = h.calculated_stage ? STAGE_LABELS[h.calculated_stage] || h.calculated_stage : '';

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">Hipertensión Arterial</h5>
        <div className="row mt-3">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Estadio</label>
              <Select
                className="w-100"
                value={h.stage || undefined}
                onChange={(v) => set('stage', v)}
                placeholder="Seleccionar..."
                options={STAGE_OPTIONS}
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Años de Diagnóstico</label>
              <InputNumber
                className="w-100"
                min={0}
                value={h.diagnosis_years}
                onChange={(v) => set('diagnosis_years', v)}
                placeholder="Años"
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">PAS Promedio (mmHg)</label>
              <InputNumber
                className="w-100"
                min={0}
                max={300}
                value={h.avg_sbp}
                onChange={(v) => set('avg_sbp', v)}
                placeholder="0-300"
              />
            </div>
          </div>
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">PAD Promedio (mmHg)</label>
              <InputNumber
                className="w-100"
                min={0}
                max={200}
                value={h.avg_dbp}
                onChange={(v) => set('avg_dbp', v)}
                placeholder="0-200"
              />
            </div>
          </div>
          <div className="col-md-4">
            {h.calculated_stage && (
              <div className="mb-3">
                <label className="form-label">Estadio Calculado</label>
                <div
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    background: calcColor,
                  }}
                >
                  {calcLabel}
                </div>
                {h.stage && h.stage !== h.calculated_stage && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 ms-2"
                    onClick={() => set('stage', h.calculated_stage)}
                  >
                    Usar calculado
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-12">
            <h6 className="text-muted fw-semibold">Daño a Órgano Blanco</h6>
          </div>
          {ORGANS.map((organ) => (
            <div className="col-md-4 mb-2" key={organ}>
              <div className="d-flex align-items-center gap-2">
                <Switch
                  checked={isOrganPresent(organ)}
                  onChange={(v) => handleOrganToggle(organ, v)}
                />
                <span className="text-muted small">{organ}</span>
              </div>
              {isOrganPresent(organ) && (
                <div className="mt-1">
                  <textarea
                    className="form-control"
                    rows={1}
                    value={getOrganDetails(organ)}
                    onChange={(e) => handleOrganDetails(organ, e.target.value)}
                    placeholder={`Detalles de ${organ}...`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="row mt-2">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Tratamiento Actual</label>
              <Select
                className="w-100"
                mode="multiple"
                value={h.current_treatment}
                onChange={(v) => set('current_treatment', v)}
                placeholder="Seleccionar tratamientos..."
                options={TREATMENT_OPTIONS}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Observaciones</label>
              <textarea
                className="form-control"
                rows={2}
                value={h.observations}
                onChange={(e) => set('observations', e.target.value)}
                placeholder="Notas adicionales sobre HTA..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HypertensionAssessment;
